import { ProfileType } from '@rma/common-ui-domain/types';

// FB docs: https://developers.facebook.com/docs/meta-pixel/reference

export const META_PIXEL_STANDARD_EVENTS = [
  'AddPaymentInfo',
  'AddToCart',
  'AddToWishlist',
  'CompleteRegistration',
  'Contact',
  'CustomizeProduct',
  'Donate',
  'FindLocation',
  'InitiateCheckout',
  'Lead',
  'Purchase',
  'Schedule',
  'Search',
  'StartTrial',
  'SubmitApplication',
  'Subscribe',
  'ViewContent',
  'PageView',
] as const;

interface ContentParam {
  id: string;
  quantity: number;
}

export type MetaPixelStandardEvents = (typeof META_PIXEL_STANDARD_EVENTS)[number];
export type MetaEventParams<E extends MetaPixelStandardEvents & keyof MetaStandardEventParams> = MetaStandardEventParams[E];
export type MetaEventCustomParams<E extends MetaPixelStandardEvents & keyof MetaStandardEventCustomParams> =
  MetaStandardEventCustomParams[E];

export interface MetaEventOptions {
  /** Identifier for deduplicating an event. Required when also sending events via conversion API */
  eventID?: string;
}

export interface MetaStandardEventProperties {
  /** Category of the page/product. */
  content_category?: string;
  /** Product IDs associated with the event, such as SKUs */
  content_ids?: string[];
  /** Name of the page/product. */
  content_name?: string;
  /**
   * Either product or product_group based on the content_ids or contents being passed.
   * If the IDs being passed in content_ids or contents parameter are IDs of products,
   * then the value should be product. If product group IDs are being passed, then the
   * value should be product_group. If no content_type is provided, Meta will match
   * the event to every item that has the same ID, independent of its type.
   */
  content_type?: 'product' | 'product_group';
  /**
   * An array of JSON objects that contains the quantity and the International Article
   * Number (EAN) when applicable, or other product or content identifier(s).
   * id and quantity are the required fields.
   */
  contents?: ContentParam[];
  /** The currency for the value specified. */
  currency?: string;
  /** The number of items when checkout was initiated. */
  num_items?: number;
  /** Predicted lifetime value of a subscriber as defined by the advertiser and expressed as an exact value. */
  predicted_ltv?: number;
  /** The string entered by the user for the search. */
  search_string?: string;
  /** Used with the CompleteRegistration event, to show the status of the registration. */
  status?: boolean;
  /** The value of a user performing this event to the business. */
  value?: number;
}

/**
 * The standard event data parameters expected for each standard event defined by facebook.
 * Each is a subset of the properties defined in MetaStandardEventProperties.
 * Some have zero expected parameters
 */
export interface MetaStandardEventParams {
  AddPaymentInfo: Pick<MetaStandardEventProperties, 'value' | 'currency'>;
  AddToCart: Pick<MetaStandardEventProperties, 'content_ids' | 'content_type' | 'contents' | 'currency' | 'value'>;
  AddToWishlist: Pick<MetaStandardEventProperties, 'content_ids' | 'content_type' | 'contents' | 'currency' | 'value'>;
  CompleteRegistration: Pick<MetaStandardEventProperties, 'currency' | 'value' | 'status'>;
  Contact: Record<PropertyKey, never>;
  CustomizeProduct: Record<PropertyKey, never>;
  Donate: Record<PropertyKey, never>;
  FindLocation: Record<PropertyKey, never>;
  InitiateCheckout: Pick<MetaStandardEventProperties, 'content_ids' | 'content_type' | 'contents' | 'currency' | 'num_items' | 'value'>;
  Lead: Pick<MetaStandardEventProperties, 'value' | 'currency'>;
  Purchase: Required<Pick<MetaStandardEventProperties, 'currency' | 'value'>> &
    Pick<MetaStandardEventProperties, 'content_ids' | 'content_type' | 'contents' | 'num_items'>;
  Schedule: Record<PropertyKey, never>;
  Search: Pick<MetaStandardEventProperties, 'search_string' | 'content_ids' | 'content_type' | 'contents' | 'currency' | 'value'>;
  StartTrial: Pick<MetaStandardEventProperties, 'value' | 'predicted_ltv' | 'currency'>;
  SubmitApplication: Record<PropertyKey, never>;
  Subscribe: Pick<MetaStandardEventProperties, 'value' | 'predicted_ltv' | 'currency'>;
  ViewContent: Pick<MetaStandardEventProperties, 'content_ids' | 'content_type' | 'contents' | 'value' | 'currency'>;
  PageView: Record<PropertyKey, never>;
}

/**
 * Custom events (and standard event custom data) can be passed any meta standard event property
 * or any custom value. This interface combines the meta standard event properties with the
 * commonly used custom ones defined by RMA (helps keep variable naming the same across events)
 */
export interface RmaCustomEventParams extends MetaStandardEventProperties {
  profile_type?: ProfileType;
  profile_code?: string;
}

/**
 * Defines the custom param hints for each standard event (ie the same as custom params, but with the standard event params for that event removed).
 * Will allow any other custom params as well
 */
export type MetaStandardEventCustomParams = {
  [E in keyof MetaStandardEventParams]: Omit<RmaCustomEventParams, keyof MetaStandardEventParams[E]> & { [key: string]: any };
};

/** User data for Advanced Matching. See: https://developers.facebook.com/docs/meta-pixel/advanced/advanced-matching#reference */
export interface MetaUserData {
  /** Email address */
  em?: string; // email
  /** First name */
  fn?: string;
  /** Last name */
  ln?: string;
  /** Phone number including country and area code */
  ph?: string;
  /** Unique id (user id) */
  external_id?: string; // your internal user id
  /** Gender ('m' or 'f'). This also allows any string in case already hashed */
  ge?: 'm' | 'f' | string;
  /** Date of birth, digits only (YYYYMMDD) */
  db?: string;
  /** City, lowercase, spaces removed (ie menlopark) */
  ct?: string;
  /** State/Region (lowercase two-letter state or province code) */
  st?: string;
  /** Zip/Postal code */
  zp?: string;
  /** Country (lowercase, 2-letter ISO code) */
  country?: string;
}
